import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Mail, Phone, Calendar, Eye, Trash2, MessageSquare, Filter } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactsManagerEnhanced = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const statusOptions = {
    new: { label: 'Nouveau', color: 'bg-blue-100 text-blue-800' },
    in_progress: { label: 'En cours', color: 'bg-yellow-100 text-yellow-800' },
    resolved: { label: 'Résolu', color: 'bg-green-100 text-green-800' },
    closed: { label: 'Fermé', color: 'bg-gray-100 text-gray-800' }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    filterContacts();
  }, [statusFilter, contacts]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contact/requests`, getAuthHeaders());
      setContacts(response.data);
      setFilteredContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Erreur lors du chargement des contacts');
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    if (statusFilter === 'all') {
      setFilteredContacts(contacts);
    } else {
      setFilteredContacts(contacts.filter(c => c.status === statusFilter));
    }
  };

  const handleViewDetails = (contact) => {
    setSelectedContact(contact);
    setShowDetailsModal(true);
  };

  const handleUpdateStatus = async (contactId, newStatus) => {
    try {
      await axios.put(
        `${API}/contact/${contactId}/status`,
        { status: newStatus },
        getAuthHeaders()
      );
      toast.success('Statut mis à jour');
      fetchContacts();
      if (selectedContact?.id === contactId) {
        setSelectedContact({ ...selectedContact, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteContact = async (contactId, contactName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le contact de "${contactName}" ?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/contact/${contactId}`, getAuthHeaders());
      toast.success('Contact supprimé avec succès');
      fetchContacts();
      if (showDetailsModal && selectedContact?.id === contactId) {
        setShowDetailsModal(false);
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusStats = () => {
    return {
      total: contacts.length,
      new: contacts.filter(c => c.status === 'new').length,
      in_progress: contacts.filter(c => c.status === 'in_progress').length,
      resolved: contacts.filter(c => c.status === 'resolved').length
    };
  };

  const stats = getStatusStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Gestion des Contacts</h1>
        <p className="text-slate-600">Gérez les demandes de contact des utilisateurs</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Total</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Nouveaux</p>
                <p className="text-3xl font-bold text-blue-600">{stats.new}</p>
              </div>
              <Badge className="bg-blue-100 text-blue-800">Nouveau</Badge>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">En cours</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.in_progress}</p>
              </div>
              <Badge className="bg-yellow-100 text-yellow-800">En cours</Badge>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Résolus</p>
                <p className="text-3xl font-bold text-green-600">{stats.resolved}</p>
              </div>
              <Badge className="bg-green-100 text-green-800">Résolu</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-slate-600" />
            <label className="text-sm font-medium text-slate-700">Filtrer par statut :</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous</option>
              {Object.entries(statusOptions).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Contacts List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Liste des contacts ({filteredContacts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <p className="text-slate-500">Chargement...</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500">Aucun contact trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Sujet</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredContacts.map((contact) => (
                    <tr key={contact.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-slate-900">{contact.name}</div>
                          <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                            <Mail className="h-3 w-3" />
                            {contact.user_email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-900 max-w-xs truncate">
                          {contact.subject || 'Sans sujet'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge className={statusOptions[contact.status]?.color}>
                          {statusOptions[contact.status]?.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar className="h-4 w-4" />
                          {formatDate(contact.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetails(contact)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteContact(contact.id, contact.name)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Modal */}
      {showDetailsModal && selectedContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Détails du contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Contact Info */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Informations</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600">Nom</p>
                    <p className="font-medium text-slate-900">{selectedContact.name}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Email</p>
                    <p className="font-medium text-slate-900">{selectedContact.user_email}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Service</p>
                    <p className="font-medium text-slate-900">{selectedContact.service || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Date</p>
                    <p className="font-medium text-slate-900">{formatDate(selectedContact.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Subject */}
              {selectedContact.subject && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Sujet</h3>
                  <p className="text-slate-700">{selectedContact.subject}</p>
                </div>
              )}

              {/* Message */}
              <div>
                <h3 className="font-semibold text-lg mb-2">Message</h3>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
              </div>

              {/* Status Update */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Changer le statut</h3>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(statusOptions).map(([key, { label, color }]) => (
                    <Button
                      key={key}
                      size="sm"
                      variant={selectedContact.status === key ? "default" : "outline"}
                      onClick={() => handleUpdateStatus(selectedContact.id, key)}
                      className={selectedContact.status === key ? "" : ""}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              {selectedContact.notes && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Notes</h3>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-slate-700 text-sm">{selectedContact.notes}</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Fermer
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 ml-auto"
                  onClick={() => handleDeleteContact(selectedContact.id, selectedContact.name)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ContactsManagerEnhanced;
